/**
 * Audio director — slice 4 of the audio runtime (docs/design/18-audio.md).
 *
 * The looping-bed state machine: it owns ambience + music transitions that the
 * one-shot trigger bridge does not. Installed once a match room exists.
 *
 *   - on install: stop menu music, crossfade in the ambience bed for the
 *     current day/night phase.
 *   - each frame: detect the day↔night phase edge → crossfade forest beds +
 *     fire the dawn/dusk announce; poll local-player survival state → frostbite
 *     shiver + a shared "meter critical" heartbeat warning.
 *   - on `match-end`: stop ambience, play victory or defeat by local team.
 *
 * Two looping layers run together in-match: the diegetic forest AMBIENCE bed
 * (nature) and the MUSIC score bed (match-day / match-night). Both crossfade on
 * the same day↔night edge but live on separate buses, so the player mixes them
 * independently. The music beds default low and sit under gameplay.
 */

import type { Room } from "colyseus.js";
import type { HelloState } from "@tribez/shared";
import type { AudioManager } from "./audio-manager";

export interface AudioDirectorOptions {
  readonly room: Room<HelloState>;
  readonly audio: AudioManager;
}

export interface AudioDirectorAPI {
  tick(dt: number): void;
  dispose(): void;
}

const FOREST_DAY = "ambience.forest-day";
const FOREST_NIGHT = "ambience.forest-night";
const MUSIC_DAY = "music.match-day";
const MUSIC_NIGHT = "music.match-night";
const CRIT_THRESHOLD = 20;   // warmth/hunger (0..100) below this → warning
const CRIT_REARM = 30;       // hysteresis: meter must recover past this to re-arm

export function installAudioDirector(opts: AudioDirectorOptions): AudioDirectorAPI {
  const { room, audio } = opts;
  const state = room.state;

  // phase: 0 = day, 1 = night (HelloState.dayNightPhase).
  let lastPhase = state.dayNightPhase;
  let frostbiteArmed = true;   // fire shiver on the rising edge of Frostbite
  let warningArmed = true;     // fire heartbeat once per critical dip
  let ended = false;

  // Begin the match soundscape.
  audio.stop("music.menu", { fadeMs: 800 });
  startBed(lastPhase);

  function startBed(phase: number): void {
    if (phase === 1) {
      audio.loop(FOREST_NIGHT, { fadeMs: 1500 });
      audio.loop(MUSIC_NIGHT, { fadeMs: 1500 });
    } else {
      audio.loop(FOREST_DAY, { fadeMs: 1500 });
      audio.loop(MUSIC_DAY, { fadeMs: 1500 });
    }
  }

  function crossfadeTo(phase: number): void {
    if (phase === 1) {
      audio.stop(FOREST_DAY, { fadeMs: 2500 });
      audio.stop(MUSIC_DAY, { fadeMs: 2500 });
      audio.loop(FOREST_NIGHT, { fadeMs: 2500 });
      audio.loop(MUSIC_NIGHT, { fadeMs: 2500 });
      audio.play("announce.dusk");
    } else {
      audio.stop(FOREST_NIGHT, { fadeMs: 2500 });
      audio.stop(MUSIC_NIGHT, { fadeMs: 2500 });
      audio.loop(FOREST_DAY, { fadeMs: 2500 });
      audio.loop(MUSIC_DAY, { fadeMs: 2500 });
      audio.play("announce.dawn");
    }
  }

  room.onMessage("match-end", (msg: { winningTeamId: number }) => {
    if (ended) return;
    ended = true;
    audio.stop(FOREST_DAY, { fadeMs: 1500 });
    audio.stop(FOREST_NIGHT, { fadeMs: 1500 });
    audio.stop(MUSIC_DAY, { fadeMs: 1500 });
    audio.stop(MUSIC_NIGHT, { fadeMs: 1500 });
    const myTeam = state.cubes.get(room.sessionId)?.teamId;
    const won = myTeam !== undefined && msg.winningTeamId === myTeam;
    audio.play(won ? "music.victory" : "music.defeat");
  });

  function survivalTick(): void {
    const cube = state.cubes.get(room.sessionId);
    if (!cube) return;

    // Frostbite shiver on onset.
    const frostbitten = cube.meta.behaviors.has("Frostbite");
    if (frostbitten && frostbiteArmed) {
      audio.play("survival.frostbite");
      frostbiteArmed = false;
    } else if (!frostbitten) {
      frostbiteArmed = true;
    }

    // Shared "meter critical" heartbeat — any survival meter dipping low.
    const low = cube.survival.warmth < CRIT_THRESHOLD || cube.survival.hunger < CRIT_THRESHOLD;
    if (low && warningArmed) {
      audio.play("survival.warning");
      warningArmed = false;
    } else if (
      !low &&
      cube.survival.warmth > CRIT_REARM &&
      cube.survival.hunger > CRIT_REARM
    ) {
      warningArmed = true;
    }
  }

  function tick(_dt: number): void {
    const phase = state.dayNightPhase;
    if (phase !== lastPhase) {
      crossfadeTo(phase);
      lastPhase = phase;
    }
    survivalTick();
  }

  function dispose(): void {
    audio.stop(FOREST_DAY, { fadeMs: 400 });
    audio.stop(FOREST_NIGHT, { fadeMs: 400 });
    audio.stop(MUSIC_DAY, { fadeMs: 400 });
    audio.stop(MUSIC_NIGHT, { fadeMs: 400 });
  }

  return { tick, dispose };
}
